/**
 * Chatbot Frontend Script
 * 
 * Handles the floating chat widget for conversational AI interactions.
 * Uses server-provided session ID and pre-loaded chat history for persistence.
 */

// Import styles - will be extracted to chatbot.css by webpack
import './chatbot.scss';

(function () {
    'use strict';

    // Configuration from WordPress (includes sessionId and chatHistory)
    const config = window.patternOrderChat || {};

    // State
    let isOpen = false;
    let isLoading = false;
    let chatHistory = [];
    let sessionId = null;

    // DOM References
    let widget, chatWindow, messagesContainer, chatForm, chatInput;

    /**
     * Get session ID from server-provided config or cookie.
     * CRITICAL: Uses PHP-provided session ID to ensure sync between PHP render and JS chat.
     * NOTE: Cookies are ONLY set by PHP, never by JS (to avoid browser blocking issues).
     */
    function getSessionId() {
        // Priority 1: Use server-provided session ID (guaranteed sync with PHP)
        if (config.sessionId) {
            sessionId = config.sessionId;
            console.log('[PatternAI] Using server-provided session:', sessionId);
            return sessionId;
        }

        // Priority 2: Read existing cookie (set by PHP)
        const cookies = document.cookie.split(';');
        for (let cookie of cookies) {
            const [name, value] = cookie.trim().split('=');
            if (name === 'pattern_ai_uid' && value) {
                sessionId = value;
                console.log('[PatternAI] Using cookie session:', sessionId);
                return sessionId;
            }
        }

        // Priority 3: No session available - will be created by PHP on next request
        // This is a rare edge case that shouldn't happen if PHP init runs properly
        console.warn('[PatternAI] No session ID available - will be created on next server request');
        return null;
    }

    /**
     * Build headers for API requests.
     * Only includes nonce for logged-in users to avoid "Cookie check failed" error.
     * Always includes session ID header for consistency.
     */
    function getApiHeaders() {
        const headers = {
            'Content-Type': 'application/json',
        };

        // Only include nonce if user is logged in
        if (config.nonce && config.isLoggedIn) {
            headers['X-WP-Nonce'] = config.nonce;
        }

        // Always include session ID for consistent session tracking
        if (sessionId) {
            headers['X-Pattern-Session'] = sessionId;
        }

        return headers;
    }

    /**
     * Initialize the chatbot
     */
    function init() {
        // Ensure session ID is set before any API calls
        getSessionId();

        // Check if PHP-rendered markup exists
        widget = document.getElementById('pattern-order-chatbot');

        if (widget) {
            // Use existing PHP-rendered markup
            initExistingMarkup();
        } else {
            // Create widget dynamically
            createChatWidget();
            widget = document.getElementById('pattern-ai-chat');
        }

        loadChatHistory();
        setupEventListeners();

        // Show demo mode indicator if active
        if (config.demoMode) {
            showDemoIndicator();
        }
    }

    /**
     * Initialize using existing PHP-rendered markup
     */
    function initExistingMarkup() {
        chatWindow = widget.querySelector('.poc-chatbot__window');
        messagesContainer = widget.querySelector('.poc-chatbot__messages');
        chatForm = widget.querySelector('.poc-chatbot__form');
        chatInput = widget.querySelector('.poc-chatbot__input');

        // Don't add greeting here - loadChatHistory will handle it
        // This prevents duplicate greeting when history is empty
    }

    /**
     * Create the chat widget HTML
     */
    function createChatWidget() {
        const widget = document.createElement('div');
        widget.id = 'pattern-ai-chat';
        widget.innerHTML = `
            <button class="chat-toggle" aria-label="Open chat">
                <svg class="chat-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                    <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"></path>
                </svg>
                <svg class="close-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" style="display:none">
                    <line x1="18" y1="6" x2="6" y2="18"></line>
                    <line x1="6" y1="6" x2="18" y2="18"></line>
                </svg>
            </button>
            <div class="chat-window" aria-hidden="true">
                <div class="chat-header">
                    <div class="chat-header-content">
                        <span class="chat-title">Chat with us</span>
                        <span class="chat-subtitle">Powered by AI</span>
                    </div>
                    <div class="chat-header-actions">
                        <button class="chat-action-btn" id="chat-reset" title="Reset personalization">
                            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" width="16" height="16">
                                <polyline points="1 4 1 10 7 10"></polyline>
                                <path d="M3.51 15a9 9 0 1 0 2.13-9.36L1 10"></path>
                            </svg>
                        </button>
                        <button class="chat-action-btn" id="chat-clear" title="Clear chat">
                            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" width="16" height="16">
                                <polyline points="3 6 5 6 21 6"></polyline>
                                <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path>
                            </svg>
                        </button>
                    </div>
                </div>
                <div class="chat-messages" id="chat-messages">
                    <div class="chat-message assistant">
                        <div class="message-content">${escapeHtml(config.greeting || 'Hi! How can I help you today?')}</div>
                    </div>
                </div>
                <form class="chat-input-form" id="chat-form">
                    <input type="text" class="chat-input" id="chat-input" placeholder="Type a message..." autocomplete="off">
                    <button type="submit" class="chat-send" id="chat-send" aria-label="Send message">
                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" width="20" height="20">
                            <line x1="22" y1="2" x2="11" y2="13"></line>
                            <polygon points="22 2 15 22 11 13 2 9 22 2"></polygon>
                        </svg>
                    </button>
                </form>
            </div>
        `;

        document.body.appendChild(widget);

        // Set references for dynamically created widget
        chatWindow = widget.querySelector('.chat-window');
        messagesContainer = widget.querySelector('#chat-messages');
        chatForm = widget.querySelector('#chat-form');
        chatInput = widget.querySelector('#chat-input');
    }

    /**
     * Setup event listeners
     */
    function setupEventListeners() {
        // Determine which markup style we're using
        const isPHPMarkup = widget.id === 'pattern-order-chatbot';

        if (isPHPMarkup) {
            // PHP-rendered markup event listeners
            const toggleBtn = widget.querySelector('.poc-chatbot__toggle');
            const minimizeBtn = widget.querySelector('.poc-chatbot__minimize');
            const resetBtn = widget.querySelector('.poc-chatbot__reset');

            if (toggleBtn) toggleBtn.addEventListener('click', toggleChat);
            if (minimizeBtn) minimizeBtn.addEventListener('click', toggleChat);
            if (resetBtn) resetBtn.addEventListener('click', resetPersonalization);
            if (chatForm) chatForm.addEventListener('submit', handleSubmit);
            if (chatInput) {
                chatInput.addEventListener('keydown', function (e) {
                    if (e.key === 'Enter' && !e.shiftKey) {
                        e.preventDefault();
                        handleSubmit(e);
                    }
                });
            }
        } else {
            // Dynamically created markup event listeners
            const toggleBtn = widget.querySelector('.chat-toggle');
            const clearBtn = document.getElementById('chat-clear');
            const resetBtn = document.getElementById('chat-reset');

            if (toggleBtn) toggleBtn.addEventListener('click', toggleChat);
            if (chatForm) chatForm.addEventListener('submit', handleSubmit);
            if (clearBtn) clearBtn.addEventListener('click', clearChat);
            if (resetBtn) resetBtn.addEventListener('click', resetPersonalization);
            if (chatInput) {
                chatInput.addEventListener('keydown', function (e) {
                    if (e.key === 'Enter' && !e.shiftKey) {
                        e.preventDefault();
                        handleSubmit(e);
                    }
                });
            }
        }
    }

    /**
     * Toggle chat window
     */
    function toggleChat() {
        isOpen = !isOpen;
        const isPHPMarkup = widget.id === 'pattern-order-chatbot';

        if (isPHPMarkup) {
            // PHP-rendered markup
            const toggleBtn = widget.querySelector('.poc-chatbot__toggle');

            if (isOpen) {
                widget.classList.add('poc-chatbot--open');
                chatWindow.setAttribute('aria-hidden', 'false');
                toggleBtn.setAttribute('aria-expanded', 'true');
                if (chatInput) chatInput.focus();
            } else {
                widget.classList.remove('poc-chatbot--open');
                chatWindow.setAttribute('aria-hidden', 'true');
                toggleBtn.setAttribute('aria-expanded', 'false');
            }
        } else {
            // Dynamically created markup
            const chatIcon = widget.querySelector('.chat-icon');
            const closeIcon = widget.querySelector('.close-icon');

            if (isOpen) {
                widget.classList.add('open');
                chatWindow.setAttribute('aria-hidden', 'false');
                if (chatIcon) chatIcon.style.display = 'none';
                if (closeIcon) closeIcon.style.display = 'block';
                if (chatInput) chatInput.focus();
            } else {
                widget.classList.remove('open');
                chatWindow.setAttribute('aria-hidden', 'true');
                if (chatIcon) chatIcon.style.display = 'block';
                if (closeIcon) closeIcon.style.display = 'none';
            }
        }
    }

    /**
     * Handle form submission
     */
    async function handleSubmit(e) {
        e.preventDefault();

        const message = chatInput ? chatInput.value.trim() : '';

        if (!message || isLoading) return;

        chatInput.value = '';
        addMessage('user', message);

        isLoading = true;
        showTypingIndicator();

        try {
            const response = await sendMessage(message);
            hideTypingIndicator();

            if (response.success) {
                addMessage('assistant', response.message);

                // Check if we should refresh page content via AJAX
                if (response.should_refresh || response.context_updated) {
                    await refreshPageContent();
                }
            } else {
                addMessage('assistant', response.message || 'Sorry, something went wrong. Please try again.');
            }
        } catch (error) {
            hideTypingIndicator();
            addMessage('assistant', 'Sorry, I encountered an error. Please try again.');
            console.error('Chat error:', error);
        }

        isLoading = false;
    }

    /**
     * Refresh page content via AJAX (no page reload).
     * Fetches updated block HTML and replaces it in the DOM.
     */
    async function refreshPageContent() {
        console.log('[PatternAI] Refreshing content via AJAX...');

        // Show subtle update indicator
        showContentUpdateNotice();

        try {
            const response = await fetchWithRetry(config.restUrl + 'refresh-blocks', {
                method: 'POST',
                headers: getApiHeaders(),
                credentials: 'same-origin',
                body: JSON.stringify({
                    post_id: config.postId
                })
            });

            const data = await response.json();

            if (data.success && data.blocks && data.blocks.length > 0) {
                // Find and replace pattern-order-change blocks in the DOM
                const existingBlocks = document.querySelectorAll('.wp-block-create-block-pattern-order-change');

                data.blocks.forEach((blockData, index) => {
                    if (existingBlocks[index]) {
                        // Create a temporary container to parse the new HTML
                        const temp = document.createElement('div');
                        temp.innerHTML = blockData.html;
                        const newBlock = temp.firstElementChild;

                        if (newBlock) {
                            // Add a subtle animation class
                            newBlock.classList.add('poc-content-updated');

                            // Replace the old block with the new one
                            existingBlocks[index].replaceWith(newBlock);

                            console.log('[PatternAI] Block', index, 'updated with personalized content');

                            // Remove animation class after animation completes
                            setTimeout(() => {
                                newBlock.classList.remove('poc-content-updated');
                            }, 1000);
                        }
                    }
                });

                hideContentUpdateNotice();
                showContentUpdatedSuccess();
            } else {
                console.log('[PatternAI] No blocks to update');
                hideContentUpdateNotice();
            }
        } catch (error) {
            console.error('[PatternAI] Failed to refresh content:', error);
            hideContentUpdateNotice();
        }
    }

    /**
     * Show subtle notice that content is being updated
     */
    function showContentUpdateNotice() {
        // Remove existing notice if any
        hideContentUpdateNotice();

        const notice = document.createElement('div');
        notice.id = 'poc-content-updating';
        notice.className = 'poc-content-notice poc-content-notice--updating';
        notice.innerHTML = `
            <span class="poc-content-notice__spinner"></span>
            <span>Personalizing content...</span>
        `;
        document.body.appendChild(notice);
    }

    /**
     * Hide the updating notice
     */
    function hideContentUpdateNotice() {
        const notice = document.getElementById('poc-content-updating');
        if (notice) {
            notice.remove();
        }
    }

    /**
     * Show success message after content update
     */
    function showContentUpdatedSuccess() {
        const notice = document.createElement('div');
        notice.className = 'poc-content-notice poc-content-notice--success';
        notice.innerHTML = `
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                <polyline points="20 6 9 17 4 12"></polyline>
            </svg>
            <span>Content personalized for you!</span>
        `;
        document.body.appendChild(notice);

        // Auto-remove after 3 seconds
        setTimeout(() => {
            notice.classList.add('poc-content-notice--fade-out');
            setTimeout(() => notice.remove(), 300);
        }, 3000);
    }

    /**
     * Fetch with retry logic for "Cookie check failed" (Nonce) errors.
     * If request fails with 403/Nonce issue, retry without credentials to fallback to session-based auth.
     */
    async function fetchWithRetry(url, options) {
        try {
            const response = await fetch(url, options);

            // If secure request failed, try falling back to anonymous/session-only
            if (!response.ok && options.credentials === 'same-origin' && response.status === 403) {
                const data = await response.clone().json().catch(() => ({}));

                // Check for specific WP Nonce error codes
                if (data.code === 'rest_cookie_invalid_nonce' || data.code === 'rest_forbidden') {
                    console.warn('[PatternAI] Nonce check failed. Retrying with session-only auth...');

                    const newOptions = { ...options };
                    newOptions.credentials = 'omit'; // Remove cookies
                    delete newOptions.headers['X-WP-Nonce']; // Remove bad nonce

                    return fetch(url, newOptions);
                }
            }

            return response;
        } catch (error) {
            throw error;
        }
    }

    /**
     * Send message to API
     */
    async function sendMessage(message) {
        const response = await fetchWithRetry(config.restUrl + 'chat', {
            method: 'POST',
            headers: getApiHeaders(),
            credentials: 'same-origin',
            body: JSON.stringify({ message }),
        });

        return response.json();
    }

    /**
     * Add message to chat
     */
    function addMessage(role, content) {
        if (!messagesContainer) return;

        const isPHPMarkup = widget.id === 'pattern-order-chatbot';
        const messageEl = document.createElement('div');

        if (isPHPMarkup) {
            messageEl.className = `poc-chatbot__message poc-chatbot__message--${role}`;
            messageEl.innerHTML = `<div class="poc-chatbot__message-content">${formatMessage(content)}</div>`;
        } else {
            messageEl.className = `chat-message ${role}`;
            messageEl.innerHTML = `<div class="message-content">${formatMessage(content)}</div>`;
        }

        messagesContainer.appendChild(messageEl);
        messagesContainer.scrollTop = messagesContainer.scrollHeight;

        chatHistory.push({ role, content });
    }

    /**
     * Format message content (handle markdown-like formatting)
     */
    function formatMessage(content) {
        // Basic markdown support
        let formatted = escapeHtml(content);

        // Bold
        formatted = formatted.replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>');

        // Links
        formatted = formatted.replace(/\[([^\]]+)\]\(([^)]+)\)/g, '<a href="$2" target="_blank" rel="noopener">$1</a>');

        // Line breaks
        formatted = formatted.replace(/\n/g, '<br>');

        return formatted;
    }

    /**
     * Escape HTML
     */
    function escapeHtml(text) {
        const div = document.createElement('div');
        div.textContent = text;
        return div.innerHTML;
    }

    /**
     * Show typing indicator
     */
    function showTypingIndicator() {
        if (!messagesContainer) return;

        const isPHPMarkup = widget.id === 'pattern-order-chatbot';
        const indicator = document.createElement('div');
        indicator.id = 'typing-indicator';

        if (isPHPMarkup) {
            indicator.className = 'poc-chatbot__message poc-chatbot__message--assistant poc-chatbot__typing';
            indicator.innerHTML = `
                <div class="poc-chatbot__message-content">
                    <span class="poc-chatbot__dot"></span>
                    <span class="poc-chatbot__dot"></span>
                    <span class="poc-chatbot__dot"></span>
                </div>
            `;
        } else {
            indicator.className = 'chat-message assistant typing-indicator';
            indicator.innerHTML = `
                <div class="message-content">
                    <span class="dot"></span>
                    <span class="dot"></span>
                    <span class="dot"></span>
                </div>
            `;
        }

        messagesContainer.appendChild(indicator);
        messagesContainer.scrollTop = messagesContainer.scrollHeight;
    }

    /**
     * Hide typing indicator
     */
    function hideTypingIndicator() {
        const indicator = document.getElementById('typing-indicator');
        if (indicator) {
            indicator.remove();
        }
    }

    /**
     * Clear chat history
     */
    async function clearChat() {
        try {
            await fetch(config.restUrl + 'chat/clear', {
                method: 'POST',
                headers: getApiHeaders(),
                credentials: 'same-origin',
            });

            // Clear UI
            const messagesContainer = document.getElementById('chat-messages');
            messagesContainer.innerHTML = `
                <div class="chat-message assistant">
                    <div class="message-content">${escapeHtml(config.greeting || 'Hi! How can I help you today?')}</div>
                </div>
            `;

            chatHistory = [];
        } catch (error) {
            console.error('Failed to clear chat:', error);
        }
    }

    /**
     * Reset personalization: clear chat session and reload page to show default content.
     */
    async function resetPersonalization() {
        try {
            // Clear the chat session on the server.
            await fetchWithRetry(config.restUrl + 'chat/clear', {
                method: 'POST',
                headers: getApiHeaders(),
                credentials: 'same-origin',
            });

            // Reload page to render default (non-personalized) content.
            window.location.reload();
        } catch (error) {
            console.error('[PatternAI] Failed to reset personalization:', error);
            addMessage('assistant', 'Sorry, failed to reset. Please try again.');
        }
    }

    /**
     * Load chat history - uses server-provided data first, falls back to API.
     * Pre-loading from server ensures immediate display and session sync.
     */
    async function loadChatHistory() {
        // Priority 1: Use pre-loaded chat history from server (instant, no API call)
        if (config.chatHistory && config.chatHistory.length > 0) {
            console.log('[PatternAI] Using pre-loaded chat history:', config.chatHistory.length, 'messages');

            if (!messagesContainer) {
                console.error('[PatternAI] Messages container not found');
                return;
            }

            // Clear default greeting
            messagesContainer.innerHTML = '';

            // Add all history messages
            config.chatHistory.forEach(entry => {
                addMessage(entry.role, entry.content);
            });

            // Store in local state
            chatHistory = [...config.chatHistory];
            return;
        }

        // Priority 2: Fetch from API (fallback if pre-load failed or history changed)
        try {
            console.log('[PatternAI] Fetching chat history from API...');
            const response = await fetch(config.restUrl + 'chat/history', {
                method: 'GET',
                headers: getApiHeaders(),
                credentials: 'same-origin',
            });

            const data = await response.json();

            if (data.history && data.history.length > 0) {
                console.log('[PatternAI] Loaded', data.history.length, 'messages from API');

                if (messagesContainer) {
                    messagesContainer.innerHTML = '';
                    data.history.forEach(entry => {
                        addMessage(entry.role, entry.content);
                    });
                    chatHistory = [...data.history];
                }
                return;
            }
        } catch (error) {
            console.warn('[PatternAI] Failed to fetch history from API:', error);
        }

        // Priority 3: Show greeting if no history (first visit)
        if (messagesContainer) {
            messagesContainer.innerHTML = '';
            addMessage('assistant', config.greeting || 'Hi! How can I help you today?');
            console.log('[PatternAI] Showing greeting (no chat history)');
        }
    }

    /**
     * Refresh chat history from server (useful after page navigation within SPA).
     * Can be called manually if needed.
     */
    async function refreshChatHistory() {
        try {
            const response = await fetch(config.restUrl + 'chat/history', {
                method: 'GET',
                headers: getApiHeaders(),
                credentials: 'same-origin',
            });

            const data = await response.json();

            if (data.history && messagesContainer) {
                messagesContainer.innerHTML = '';
                chatHistory = [];
                data.history.forEach(entry => {
                    addMessage(entry.role, entry.content);
                });
                console.log('[PatternAI] Chat history refreshed:', data.history.length, 'messages');
            }
        } catch (error) {
            console.error('[PatternAI] Failed to refresh history:', error);
        }
    }

    // Expose refreshChatHistory for external use
    window.patternAIChatRefresh = refreshChatHistory;

    /**
     * Show demo mode indicator
     */
    function showDemoIndicator() {
        const indicator = document.createElement('div');
        indicator.className = 'demo-mode-indicator';
        indicator.innerHTML = `
            <span>Demo Mode: <strong>${config.demoIndustry}</strong></span>
            <a href="?demo=off" class="demo-off">Exit Demo</a>
        `;
        document.body.appendChild(indicator);
    }

    // Initialize when DOM is ready
    if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', init);
    } else {
        init();
    }
})();
